import React, { useEffect } from 'react';
import { useFieldArray } from 'react-hook-form';
import {
  Grid,
  TextField,
  Typography,
  Box,
  Button,
  IconButton,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const TargetExposureConfig = ({ control, register, watch, setValue, targetIndex, availableFilters = [], availableReadoutModes = [], defaultReadoutMode = null }) => {
  // Only show readout mode selector when camera has multiple modes
  const showReadoutMode = availableReadoutModes.length > 1;
  // Handle both single target (targetIndex === null) and multi-target cases
  const fieldArrayName = targetIndex !== null 
    ? `targets.${targetIndex}.exposureConfigs` 
    : 'exposures';

  const { fields, append, remove } = useFieldArray({
    control,
    name: fieldArrayName
  });

  // Ensure at least one exposure configuration exists
  useEffect(() => {
    if (fields.length === 0) {
      append({
        filter: 'L',
        exposureTime: 300,
        exposureCount: 1,
        binning: '1',
        gain: null,
        offset: null,
        readoutMode: defaultReadoutMode,
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addExposure = () => {
    append({
      filter: 'L',
      exposureTime: 300,
      exposureCount: 1,
      binning: '1',
      gain: null,
      offset: null,
      readoutMode: defaultReadoutMode,
    });
  };

  const duplicateExposure = (index) => {
    // Get current form values using watch (fields[index] only has default values)
    const fieldPath = targetIndex !== null
      ? `targets.${targetIndex}.exposureConfigs.${index}`
      : `exposures.${index}`;

    const currentValues = watch(fieldPath);

    append({
      filter: currentValues?.filter || 'L',
      exposureTime: currentValues?.exposureTime ?? 300,   // keep a 0 (camera minimum) when copying
      exposureCount: currentValues?.exposureCount ?? 1,   // keep a 0 (until window ends) when copying
      binning: currentValues?.binning || '1',
      gain: currentValues?.gain ?? null,
      offset: currentValues?.offset ?? null,
      readoutMode: currentValues?.readoutMode || defaultReadoutMode || null,
    });
  };

  const removeExposure = (index) => {
    remove(index);
  };

  // Get default filter options if none provided
  const filterOptions = availableFilters.length > 0 ? availableFilters : [
    { value: 'L', label: 'Luminance (L)', color: '#ffffff' },
    { value: 'R', label: 'Red (R)', color: '#ff0000' },
    { value: 'G', label: 'Green (G)', color: '#00ff00' },
    { value: 'B', label: 'Blue (B)', color: '#0000ff' },
    { value: 'Ha', label: 'H-alpha (Ha)', color: '#ff6b6b' },
    { value: 'OIII', label: 'Oxygen III (OIII)', color: '#4ecdc4' },
    { value: 'SII', label: 'Sulfur II (SII)', color: '#ffe66d' },
    { value: 'Clear', label: 'Clear/No Filter', color: '#e0e0e0' },
  ];

  // Calculate total exposure time
  const calculateTotalTime = () => {
    let total = 0;
    fields.forEach((field, index) => {
      const fieldPath = targetIndex !== null 
        ? `targets.${targetIndex}.exposureConfigs.${index}`
        : `exposures.${index}`;
      const time = watch(`${fieldPath}.exposureTime`) || watch(`${fieldPath}.exposure_time`) || 0;
      const count = watch(`${fieldPath}.exposureCount`) || watch(`${fieldPath}.exposure_count`) || 0;
      total += time * count;
    });
    const repeatCountPath = targetIndex !== null 
      ? `targets.${targetIndex}.sequenceRepeatCount` 
      : 'sequenceRepeatCount';
    const repeatCount = watch(repeatCountPath) || 1;
    return total * repeatCount;
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2">
          Exposure Configuration
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={addExposure}
          variant="outlined"
        >
          Add Filter
        </Button>
      </Box>

      {fields.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No exposures configured. Click "Add Filter" to start.
        </Typography>
      )}

      {fields.map((field, index) => {
        const fieldPath = targetIndex !== null 
          ? `targets.${targetIndex}.exposureConfigs.${index}`
          : `exposures.${index}`;
        // A count of 0 means "repeat until the window ends". Only the server sets it (when it turns
        // a rise-to-set request into a time-window run, along with fill_time), so accept 0 only on a
        // row that was loaded with 0, i.e. when editing such a run. `field` holds the row's loaded
        // values; new rows start at 1, so a 0 can't be typed in on create.
        const minExposureCount = Number(field.exposureCount) === 0 ? 0 : 1;

        return (
        <Paper key={field.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter</InputLabel>
                <Select
                  value={watch(`${fieldPath}.filter`) || 'L'}
                  onChange={(e) => setValue(`${fieldPath}.filter`, e.target.value)}
                  label="Filter"
                >
                  {filterOptions.map(filter => (
                    <MenuItem key={filter.value} value={filter.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            backgroundColor: filter.color,
                            border: '1px solid #999',
                            borderRadius: '2px'
                          }}
                        />
                        {filter.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Time (s)"
                {...register(`${fieldPath}.exposureTime`, {
                  valueAsNumber: true,
                  min: 0
                })}
                inputProps={{ min: 0, step: 'any' }}
                helperText={watch(`${fieldPath}.exposureTime`) === 0 ? 'Camera minimum' : undefined}
              />
            </Grid>

            <Grid item xs={6} sm={3} md={1.5}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Count"
                {...register(`${fieldPath}.exposureCount`, {
                  valueAsNumber: true,
                  min: minExposureCount
                })}
                inputProps={{ min: minExposureCount }}
                helperText={watch(`${fieldPath}.exposureCount`) === 0 ? 'Until window ends' : undefined}
              />
            </Grid>

            <Grid item xs={6} sm={3} md={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Bin</InputLabel>
                <Select
                  value={watch(`${fieldPath}.binning`) || '1'}
                  onChange={(e) => setValue(`${fieldPath}.binning`, e.target.value)}
                  label="Bin"
                >
                  <MenuItem value="1">1x1</MenuItem>
                  <MenuItem value="2">2x2</MenuItem>
                  <MenuItem value="3">3x3</MenuItem>
                  <MenuItem value="4">4x4</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6} sm={3} md={1.5}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Gain"
                {...register(`${fieldPath}.gain`, {
                  valueAsNumber: true,
                  min: 0
                })}
                inputProps={{ min: 0, max: 300 }}
              />
            </Grid>

            <Grid item xs={6} sm={3} md={1.5}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Offset"
                {...register(`${fieldPath}.offset`, {
                  valueAsNumber: true,
                  min: 0
                })}
                inputProps={{ min: 0, max: 300 }}
              />
            </Grid>

            {showReadoutMode && (
              <Grid item xs={6} sm={3} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Readout Mode</InputLabel>
                  <Select
                    value={watch(`${fieldPath}.readoutMode`) || ''}
                    onChange={(e) => setValue(`${fieldPath}.readoutMode`, e.target.value || null)}
                    label="Readout Mode"
                  >
                    <MenuItem value="">Default</MenuItem>
                    {availableReadoutModes.map(mode => (
                      <MenuItem key={mode} value={mode}>{mode}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} sm={6} md={showReadoutMode ? 1 : 2}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Duplicate this exposure">
                  <IconButton
                    size="small"
                    onClick={() => duplicateExposure(index)}
                    color="primary"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove this exposure">
                  <IconButton
                    size="small"
                    onClick={() => removeExposure(index)}
                    color="error"
                    disabled={fields.length === 1}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>

            {/* Show subtotal for this filter */}
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Subtotal: {formatTime(
                  (watch(`${fieldPath}.exposureTime`) || 0) * 
                  (watch(`${fieldPath}.exposureCount`) || 0)
                )}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      );
      })}

      {/* Sequence repeat count */}
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Sequence Repeat Count"
            {...register(targetIndex !== null ? `targets.${targetIndex}.sequenceRepeatCount` : 'sequenceRepeatCount', {
              valueAsNumber: true,
              min: 1
            })}
            inputProps={{ min: 1 }}
            helperText="Repeat the entire sequence this many times"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box sx={{ 
            p: 2, 
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1
          }}>
            <Typography variant="body2" color="text.secondary">
              Total Exposure Time
            </Typography>
            <Typography variant="h6">
              {formatTime(calculateTotalTime())}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {fields.length} filter{fields.length !== 1 ? 's' : ''} × {watch(targetIndex !== null ? `targets.${targetIndex}.sequenceRepeatCount` : 'sequenceRepeatCount') || 1} repeat{(watch(targetIndex !== null ? `targets.${targetIndex}.sequenceRepeatCount` : 'sequenceRepeatCount') || 1) !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TargetExposureConfig;